import type { OhlcvPoint, PricePoint } from "~types/types";

const OHLCV_FIELDS = {
  OPEN: "open",
  PX_OPEN: "open",
  CLOSE: "close",
  PX_LAST: "close",
  LOW: "low",
  PX_LOW: "low",
  HIGH: "high",
  PX_HIGH: "high",
  VOLUME: "volume",
  PX_VOLUME: "volume",
} as const;

type SeriesItem = Record<string, string | number>;

function parseTimestamp(item: SeriesItem): number {
  return new Date((item.date as string) || (item.dateTime as string)).getTime();
}

function buildValueMap(series: SeriesItem[]): Map<number, number> {
  const map = new Map<number, number>();

  for (const item of series) {
    map.set(parseTimestamp(item), Number(item.value));
  }

  return map;
}

function parseSingleSeries(
  series: SeriesItem[],
  volumeSeries?: SeriesItem[]
): { data: PricePoint[] } {
  const volumeMap = volumeSeries ? buildValueMap(volumeSeries) : undefined;

  const pricePoints: PricePoint[] = series.map((item) => {
    const timestamp = parseTimestamp(item);

    const point: PricePoint = {
      timestamp,
      value: Number(item.value),
      volume: volumeMap?.get(timestamp) ?? 0,
    };

    return point;
  });

  return { data: pricePoints };
}

function parseOhlcvSeries(data: Record<string, string | SeriesItem[]>): {
  data: OhlcvPoint[];
} {
  const fieldMaps: Record<string, Map<number, number>> = {};

  for (const [key, field] of Object.entries(OHLCV_FIELDS)) {
    const seriesData = data[key];

    if (Array.isArray(seriesData) && seriesData.length > 0) {
      const existing = fieldMaps[field];

      if (!existing) {
        fieldMaps[field] = buildValueMap(seriesData);
      }
    }
  }

  const timestampSet = new Set<number>();

  for (const key of Object.keys(fieldMaps)) {
    // skip volume timestamps, its supplementary data
    if (key.toUpperCase() === "VOLUME") {
      continue;
    }

    const map = fieldMaps[key];

    for (const ts of map.keys()) {
      timestampSet.add(ts);
    }
  }

  const timestamps = Array.from(timestampSet).sort((a, b) => a - b);

  const ohlcvData: OhlcvPoint[] = timestamps.map((timestamp) => ({
    timestamp,
    open: fieldMaps.open?.get(timestamp) ?? 0,
    high: fieldMaps.high?.get(timestamp) ?? 0,
    low: fieldMaps.low?.get(timestamp) ?? 0,
    close: fieldMaps.close?.get(timestamp) ?? 0,
    volume: fieldMaps.volume?.get(timestamp) ?? 0,
  }));

  return { data: ohlcvData };
}

function hasOhlcvFields(data: Record<string, string | SeriesItem[]>): boolean {
  const priceFields = ["OPEN", "PX_OPEN", "HIGH", "PX_HIGH", "LOW", "PX_LOW"];

  // Require at least one OHLC price field (not just CLOSE+VOLUME)
  return priceFields.some(
    (key) => Array.isArray(data[key]) && data[key].length > 0
  );
}

export const priceMovements = (
  data: Record<string, string | SeriesItem[]>
): { data: PricePoint[] | OhlcvPoint[] } => {
  if (!data) {
    return { data: [] };
  }

  // OHLCV series: when OPEN, CLOSE, HIGH, LOW, VOLUME fields are present
  if (hasOhlcvFields(data)) {
    return parseOhlcvSeries(data);
  }

  // Extract volume companion array if present
  let volumeArray: SeriesItem[] | undefined;

  if (Array.isArray(data.VOLUME) && data.VOLUME.length > 0) {
    volumeArray = data.VOLUME;
  } else if (Array.isArray(data.PX_VOLUME) && data.PX_VOLUME.length > 0) {
    volumeArray = data.PX_VOLUME;
  }

  // Single series: 'series', 'CLOSE', or 'PX_LAST' array
  if (Array.isArray(data.series) && data.series.length > 0) {
    return parseSingleSeries(data.series, volumeArray);
  }

  if (Array.isArray(data.CLOSE) && data.CLOSE.length > 0) {
    return parseSingleSeries(data.CLOSE, volumeArray);
  }

  if (Array.isArray(data.PX_LAST) && data.PX_LAST.length > 0) {
    return parseSingleSeries(data.PX_LAST, volumeArray);
  }

  return { data: [] };
};
